import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckBox as CheckBoxIcon, CheckBoxOutlineBlank, Close, Public } from '@mui/icons-material';
import { Autocomplete, Box, Button, Checkbox, Divider, FormControl, IconButton, Link, MenuItem, Select, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DateFilterType, calculateDateFilter } from '@frontend/features/cases/utils/dateFilters';

import { CaseStatus } from '@bandido/shared/constants';
import { OriginatedByChannel } from '@bandido/shared/types';

import { useGetAllClients } from '@frontend/features/clients/queries';
import { useGetAllUsers } from '@frontend/features/users/queries';
import { useGetAllTeams } from '@frontend/features/teams/queries';
import { useGetAllWorkflowsQuery, useGetStepsByManyWorkflowIds } from '@frontend/features/workflows/queries';
import { StepOptionsResponseDto, TeamResponseDto, WorkflowResponseDto, UserListResponseDto, ClientResponseDto } from '@bandido/shared/dtos';
import { FilterItem } from '@frontend/features/common/components/FilterItem';
import { NimbuxAutocomplete } from '@frontend/features/common/components/NimbuxAutocomplete';
import { useGetFieldBlockByWorkflowIds } from '@frontend/features/custom-fields/queries';
import { useGetMyAgencies } from '@frontend/features/agencies/queries';
import { useGetTokenOrganizations } from '@frontend/features/organizations/queries';
import { useDebouncedState } from '@frontend/features/common/hooks/useDebouncedState';

interface Props {
  title: string;
  searchParams: URLSearchParams;
  updateFilters: (filters: Record<string, string>) => void;
  clearFilters: () => void;
  onCreate?: (values: { name: string; isPublic: boolean }) => Promise<void>
  onClose?: () => void;
}

export const CaseListFilersOptions = ({ 
  title, 
  searchParams, 
  updateFilters,
  clearFilters,
  onCreate, 
  onClose 
}: Props) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const toggleIsPublic = () => setIsPublic(s => !s);

  const [teamSearch, setTeamSearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [agentSearch, setAgentSearch] = useState('');
  const [workflowSearch, setWorkflowSearch] = useState('');
  const [stepSearch, setStepSearch] = useState('');
  
  // Nuevos estados simplificados para filtros de fecha
  const [createdDateType, setCreatedDateType] = useState<string>('');
  const [updatedDateType, setUpdatedDateType] = useState<string>('');
  const [customDaysCreated, setCustomDaysCreated] = useState('');
  const [customDaysUpdated, setCustomDaysUpdated] = useState('');
  const [specificDateCreated, setSpecificDateCreated] = useState('');
  const [specificDateUpdated, setSpecificDateUpdated] = useState('');
  const [dateRangeCreated, setDateRangeCreated] = useState({ from: '', to: '' });
  const [dateRangeUpdated, setDateRangeUpdated] = useState({ from: '', to: '' });

  // Estados debounced para rangos de fechas
  const [debouncedDateRangeCreated, setDebouncedDateRangeCreated] = useDebouncedState(
    { from: '', to: '' }, 
    800
  );
  const [debouncedDateRangeUpdated, setDebouncedDateRangeUpdated] = useDebouncedState(
    { from: '', to: '' }, 
    800
  );

  // Estados para custom date fields
  const [customDateFieldStates, setCustomDateFieldStates] = useState<Record<string, {
    type: string;
    customDays: string;
    nextCustomDays: string;
    specificDate: string;
    dateRange: { from: string; to: string };
  }>>({});

  // Estados debounced para custom date fields
  const [debouncedCustomDateFieldStates, setDebouncedCustomDateFieldStates] = useDebouncedState<
    Record<string, { from: string; to: string }>
  >({}, 800);
  
  // Estados debounced para fechas específicas
  const [debouncedSpecificDateCreated, setDebouncedSpecificDateCreated] = useDebouncedState('', 800);
  const [debouncedSpecificDateUpdated, setDebouncedSpecificDateUpdated] = useDebouncedState('', 800);
  
  // Estados debounced para custom date fields - fecha específica
  const [debouncedCustomDateFieldSpecificDates, setDebouncedCustomDateFieldSpecificDates] = useDebouncedState<
    Record<string, string>
  >({}, 800);

  // Estado debounced para nextCustomDays en custom date fields
  const [debouncedCustomDateFieldNextCustomDays, setDebouncedCustomDateFieldNextCustomDays] = useDebouncedState<
    Record<string, string>
  >({}, 800);

  // Ref para controlar si ya se inicializó desde searchParams
  const isInitializedRef = useRef(false);
  
  // Ref para trackear el valor anterior de workflowIds
  const previousWorkflowIdsRef = useRef<string | null>(null);

  // Inicializar valores desde searchParams
  useEffect(() => {
    const createdFilter = searchParams.get('createdDateFilter');
    const updatedFilter = searchParams.get('updatedDateFilter');
    
    if (createdFilter) {
      setCreatedDateType(createdFilter);
      
      if (createdFilter === 'customDays') {
        setCustomDaysCreated(searchParams.get('createdCustomDays') || '');
      } else if (createdFilter === 'specificDate') {
        const specificDate = searchParams.get('createdSpecificDate') || '';
        setSpecificDateCreated(specificDate);
        setDebouncedSpecificDateCreated(specificDate);
              } else if (createdFilter === 'dateRange') {
          // Para dateRange, derivar desde after y before
          const after = searchParams.get('createdAfter') || '';
          const before = searchParams.get('createdBefore') || '';
          const dateRange = {
            from: after ? after.split('T')[0] : '', // Extraer fecha de "2025-07-01T00:00:00"
            to: before ? before.split('T')[0] : ''   // Extraer fecha de "2025-07-31T23:59:59"
          };
          setDateRangeCreated(dateRange);
          setDebouncedDateRangeCreated(dateRange);
        }
    }
    
    if (updatedFilter) {
      setUpdatedDateType(updatedFilter);
      
      if (updatedFilter === 'customDays') {
        setCustomDaysUpdated(searchParams.get('updatedCustomDays') || '');
      } else if (updatedFilter === 'specificDate') {
        const specificDate = searchParams.get('updatedSpecificDate') || '';
        setSpecificDateUpdated(specificDate);
        setDebouncedSpecificDateUpdated(specificDate);
              } else if (updatedFilter === 'dateRange') {
          // Para dateRange, derivar desde after y before
          const after = searchParams.get('updatedAfter') || '';
          const before = searchParams.get('updatedBefore') || '';
          const dateRange = {
            from: after ? after.split('T')[0] : '', // Extraer fecha de "2025-07-01T00:00:00"
            to: before ? before.split('T')[0] : ''   // Extraer fecha de "2025-07-31T23:59:59"
          };
          setDateRangeUpdated(dateRange);
          setDebouncedDateRangeUpdated(dateRange);
        }
    }

    // Inicializar custom date fields desde searchParams
    const customDateStates: Record<string, any> = {};
    const debouncedRanges: Record<string, { from: string; to: string }> = {};
    const debouncedSpecificDates: Record<string, string> = {};
    const debouncedNextCustomDays: Record<string, string> = {};
    
    for (const [key, value] of searchParams.entries()) {
      // Buscar parámetros que sigan el patrón fieldBlockId_fieldName_filter
      const filterMatch = key.match(/^(.+)_filter$/);
      if (filterMatch) {
        const baseKey = filterMatch[1];
        const filterType = value;
        
        if (filterType) {
          const specificDate = searchParams.get(`${baseKey}_specificDate`) || '';
          const nextCustomDays = searchParams.get(`${baseKey}_nextCustomDays`) || '';
          
          // Para dateRange, derivar desde after y before
          const after = searchParams.get(`${baseKey}_after`) || '';
          const before = searchParams.get(`${baseKey}_before`) || '';
          const dateRange = {
            from: after ? after.split('T')[0] : '', // Extraer fecha de "2025-07-01T00:00:00"
            to: before ? before.split('T')[0] : ''   // Extraer fecha de "2025-07-31T23:59:59"
          };
          
          customDateStates[baseKey] = {
            type: filterType,
            customDays: searchParams.get(`${baseKey}_customDays`) || '',
            nextCustomDays,
            specificDate,
            dateRange
          };
          
          // Inicializar estados debounced
          if (filterType === 'dateRange' && (dateRange.from || dateRange.to)) {
            debouncedRanges[baseKey] = dateRange;
          }
          if (filterType === 'specificDate' && specificDate) {
            debouncedSpecificDates[baseKey] = specificDate;
          }
          if (filterType === 'nextCustomDays' && nextCustomDays) {
            debouncedNextCustomDays[baseKey] = nextCustomDays;
          }
        }
      }
    }
    
    if (Object.keys(customDateStates).length > 0) {
      setCustomDateFieldStates(customDateStates);
    }
    
    // Inicializar estados debounced para custom date fields
    if (Object.keys(debouncedRanges).length > 0) {
      setDebouncedCustomDateFieldStates(debouncedRanges);
    }
    if (Object.keys(debouncedSpecificDates).length > 0) {
      setDebouncedCustomDateFieldSpecificDates(debouncedSpecificDates);
    }
    if (Object.keys(debouncedNextCustomDays).length > 0) {
      setDebouncedCustomDateFieldNextCustomDays(debouncedNextCustomDays);
    }
    
    // Marcar como inicializado después de procesar searchParams
    isInitializedRef.current = true;
  }, [searchParams]);

  // Efecto para limpiar custom field filters cuando cambie workflowIds
  useEffect(() => {
    const currentWorkflowIds = searchParams.get('workflowIds');
    
    // Si ya se inicializó y workflowIds cambió, limpiar custom field filters
    if (isInitializedRef.current && previousWorkflowIdsRef.current !== null && 
        previousWorkflowIdsRef.current !== currentWorkflowIds) {
      
      // Encontrar todos los parámetros que son custom fields
      const customFieldParams: Record<string, string> = {};
      
      for (const [key] of searchParams.entries()) {
        // Buscar parámetros que contengan UUID (fieldBlockId) seguido de _
        if (key.match(/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}_/)) {
          customFieldParams[key] = '';
        }
      }
      
      // Si hay custom field params, limpiarlos
      if (Object.keys(customFieldParams).length > 0) {
        updateFilters(customFieldParams);
        
        // También limpiar los estados locales
        setCustomDateFieldStates({});
        setDebouncedCustomDateFieldStates({});
        setDebouncedCustomDateFieldSpecificDates({});
        setDebouncedCustomDateFieldNextCustomDays({});
      }
    }
    
    // Actualizar el ref con el valor actual
    previousWorkflowIdsRef.current = currentWorkflowIds;
  }, [searchParams.get('workflowIds')]);

  // Efecto para manejar cambios en dateRangeCreated debounced
  useEffect(() => {
    if (isInitializedRef.current && createdDateType === 'dateRange' && 
        (debouncedDateRangeCreated.from || debouncedDateRangeCreated.to)) {
      handleDateFilterChange('created', 'dateRange', debouncedDateRangeCreated);
    }
  }, [debouncedDateRangeCreated, createdDateType]);

  // Efecto para manejar cambios en dateRangeUpdated debounced
  useEffect(() => {
    if (isInitializedRef.current && updatedDateType === 'dateRange' && 
        (debouncedDateRangeUpdated.from || debouncedDateRangeUpdated.to)) {
      handleDateFilterChange('updated', 'dateRange', debouncedDateRangeUpdated);
    }
  }, [debouncedDateRangeUpdated, updatedDateType]);

  // Efecto para manejar cambios en custom date fields debounced
  useEffect(() => {
    if (isInitializedRef.current) {
      Object.keys(debouncedCustomDateFieldStates).forEach(fieldKey => {
        const debouncedRange = debouncedCustomDateFieldStates[fieldKey];
        const firstUnderscoreIndex = fieldKey.indexOf('_');
        const fieldBlockId = fieldKey.substring(0, firstUnderscoreIndex);
        const fieldName = fieldKey.substring(firstUnderscoreIndex + 1);
        const currentState = customDateFieldStates[fieldKey];
        
        if (currentState && currentState.type === 'dateRange' && 
            (debouncedRange.from || debouncedRange.to)) {
          
          // Verificar si los valores actuales en la URL son diferentes antes de actualizar
          const currentAfter = searchParams.get(`${fieldKey}_after`) || '';
          const currentBefore = searchParams.get(`${fieldKey}_before`) || '';
          
          const expectedAfter = debouncedRange.from ? `${debouncedRange.from}T00:00:00` : '';
          const expectedBefore = debouncedRange.to ? `${debouncedRange.to}T23:59:59` : '';
          
          if (currentAfter !== expectedAfter || currentBefore !== expectedBefore) {
            handleCustomDateFieldChange(fieldBlockId, fieldName, 'dateRange', debouncedRange);
          }
        }
      });
    }
  }, [debouncedCustomDateFieldStates, customDateFieldStates]);

  // Efecto para manejar cambios en fecha específica creada debounced
  useEffect(() => {
    // Solo ejecutar después de la inicialización y si hay un valor
    if (isInitializedRef.current && createdDateType === 'specificDate' && debouncedSpecificDateCreated) {
      handleDateFilterChange('created', 'specificDate', debouncedSpecificDateCreated);
    }
  }, [debouncedSpecificDateCreated, createdDateType]);

  // Efecto para manejar cambios en fecha específica actualizada debounced
  useEffect(() => {
    // Solo ejecutar después de la inicialización y si hay un valor
    if (isInitializedRef.current && updatedDateType === 'specificDate' && debouncedSpecificDateUpdated) {
      handleDateFilterChange('updated', 'specificDate', debouncedSpecificDateUpdated);
    }
  }, [debouncedSpecificDateUpdated, updatedDateType]);

  // Efecto para manejar cambios en custom date fields - fecha específica debounced
  useEffect(() => {
    if (isInitializedRef.current) {
      Object.keys(debouncedCustomDateFieldSpecificDates).forEach(fieldKey => {
        const debouncedDate = debouncedCustomDateFieldSpecificDates[fieldKey];
        const firstUnderscoreIndex = fieldKey.indexOf('_');
        const fieldBlockId = fieldKey.substring(0, firstUnderscoreIndex);
        const fieldName = fieldKey.substring(firstUnderscoreIndex + 1);
        const currentState = customDateFieldStates[fieldKey];
        
        if (currentState && currentState.type === 'specificDate' && debouncedDate) {
          handleCustomDateFieldChange(fieldBlockId, fieldName, 'specificDate', debouncedDate);
        }
      });
    }
  }, [debouncedCustomDateFieldSpecificDates, customDateFieldStates]);

  // Efecto para manejar cambios en custom date fields - nextCustomDays debounced
  useEffect(() => {
    if (isInitializedRef.current) {
      Object.keys(debouncedCustomDateFieldNextCustomDays).forEach(fieldKey => {
        const debouncedDays = debouncedCustomDateFieldNextCustomDays[fieldKey];
        const firstUnderscoreIndex = fieldKey.indexOf('_');
        const fieldBlockId = fieldKey.substring(0, firstUnderscoreIndex);
        const fieldName = fieldKey.substring(firstUnderscoreIndex + 1);
        const currentState = customDateFieldStates[fieldKey];
        
        if (currentState && currentState.type === 'nextCustomDays' && debouncedDays) {
          handleCustomDateFieldChange(fieldBlockId, fieldName, 'nextCustomDays', debouncedDays);
        }
      });
    }
  }, [debouncedCustomDateFieldNextCustomDays, customDateFieldStates]);

  const handleClearFilters = () => {
    clearFilters();
    // Reset estados de filtros de fecha
    setCreatedDateType('');
    setUpdatedDateType('');
    setCustomDaysCreated('');
    setCustomDaysUpdated('');
    setSpecificDateCreated('');
    setSpecificDateUpdated('');
    setDateRangeCreated({ from: '', to: '' });
    setDateRangeUpdated({ from: '', to: '' });
    // Reset custom date fields
    setCustomDateFieldStates({});
    // Reset filtros de búsqueda
    setClientSearch('');
    setTeamSearch('');
    setAgentSearch('');
    setWorkflowSearch('');
    setStepSearch('');
    // Reset estados debounced
    setDebouncedDateRangeCreated({ from: '', to: '' });
    setDebouncedDateRangeUpdated({ from: '', to: '' });
    setDebouncedSpecificDateCreated('');
    setDebouncedSpecificDateUpdated('');
    setDebouncedCustomDateFieldStates({});
    setDebouncedCustomDateFieldSpecificDates({});
    setDebouncedCustomDateFieldNextCustomDays({});
    // Reset flag de inicialización
    isInitializedRef.current = false;
  };

  const { data: agencies } = useGetMyAgencies({});

  const { data: organizations } = useGetTokenOrganizations();

  const { data: clientList } = useGetAllClients({
    pageSize: 50,
    currentPage: 1,
    search: clientSearch,
    clientName: '',
    sort: 'firstName',
    order: 'ASC',

  });

  const { data: agentList } = useGetAllUsers({
    pageSize: 50,
    currentPage: 1,
    search: agentSearch,
  });

  const { data: teams } = useGetAllTeams({
    pageSize: 50,
    currentPage: 1,
    search: teamSearch,
  });

  const { data: workflows } = useGetAllWorkflowsQuery({
    pageSize: 50,
    currentPage: 1,
    search: workflowSearch,
  });

  const { data: steps } = useGetStepsByManyWorkflowIds({
    search: stepSearch,
    workflowIds: searchParams.get('workflowIds')?.split('|') || [],
  });

  const notSelectedOption = {
    id: 'notselected',
    firstName: t('common.unassigned'),
    lastName: '',
  };

  const notSelectedTeamOption = {
    id: 'notselected',
    name: t('common.unassigned'),
  };
  const { data: customFields } = useGetFieldBlockByWorkflowIds({
    workflows: searchParams.get('workflowIds') || undefined
  });

  const statusList = [
    {
      id: CaseStatus.canceled,
      name: t(`cases.status.${CaseStatus.canceled}`),
    },
    {
      id: CaseStatus.done,
      name: t(`cases.status.${CaseStatus.done}`),
    },
    {
      id: CaseStatus.inProgress,
      name: t(`cases.status.${CaseStatus.inProgress}`),
    },
    {
      id: CaseStatus.needsAgentAction,
      name: t(`cases.status.${CaseStatus.needsAgentAction}`),
    },
    {
      id: CaseStatus.needsClientAction,
      name: t(`cases.status.${CaseStatus.needsClientAction}`),
    },
    {
      id: CaseStatus.open,
      name: t(`cases.status.${CaseStatus.open}`),
    },
  ];

  const originatedByChannelList = [
    {
      id: OriginatedByChannel.WHATSAPP,
      name: t(`cases.originatedByChannel.${OriginatedByChannel.WHATSAPP}`),
    },
    {
      id: OriginatedByChannel.CALL,
      name: t(`cases.originatedByChannel.${OriginatedByChannel.CALL}`),
    },
    {
      id: OriginatedByChannel.EMAIL,
      name: t(`cases.originatedByChannel.${OriginatedByChannel.EMAIL}`),
    },
    {
      id: OriginatedByChannel.MANUAL,
      name: t(`cases.originatedByChannel.${OriginatedByChannel.MANUAL}`),
    },
    {
      id: OriginatedByChannel.EXTERNAL_INTEGRATION,
      name: t(`cases.originatedByChannel.${OriginatedByChannel.EXTERNAL_INTEGRATION}`),
    },
    {
      id: OriginatedByChannel.UPLOADED,
      name: t(`cases.originatedByChannel.${OriginatedByChannel.UPLOADED}`),
    },
    {
      id: OriginatedByChannel.SUPPORT_FORM,
      name: t(`cases.originatedByChannel.${OriginatedByChannel.SUPPORT_FORM}`),
    },
  ];

  // Opciones para los filtros de fecha de creación/actualización (solo pasado)
  const pastDateFilterOptions = [
    { value: 'today', label: t('filters.date.today') },
    { value: 'yesterday', label: t('filters.date.yesterday') },
    { value: 'thisWeek', label: t('filters.date.thisWeek') },
    { value: 'thisMonth', label: t('filters.date.thisMonth') },
    { value: 'lastWeek', label: t('filters.date.lastWeek') },
    { value: 'lastMonth', label: t('filters.date.lastMonth') },
    { value: 'customDays', label: t('filters.date.lastNDays') },
    { value: 'specificDate', label: t('filters.date.specificDate') },
    { value: 'dateRange', label: t('filters.date.dateRange') }
  ];

  // Opciones para los filtros de fecha de custom fields (pasado y futuro)
  const allDateFilterOptions = [
    { value: 'today', label: t('filters.date.today') },
    { value: 'yesterday', label: t('filters.date.yesterday') },
    { value: 'thisWeek', label: t('filters.date.thisWeek') },
    { value: 'thisMonth', label: t('filters.date.thisMonth') },
    { value: 'lastWeek', label: t('filters.date.lastWeek') },
    { value: 'lastMonth', label: t('filters.date.lastMonth') },
    { value: 'customDays', label: t('filters.date.lastNDays') },
    { value: 'tomorrow', label: t('filters.date.tomorrow') },
    { value: 'nextWeek', label: t('filters.date.nextWeek') },
    { value: 'nextMonth', label: t('filters.date.nextMonth') },
    { value: 'nextCustomDays', label: t('filters.date.nextNDays') },
    { value: 'specificDate', label: t('filters.date.specificDate') },
    { value: 'dateRange', label: t('filters.date.dateRange') }
  ];

  // Función para manejar cambios en filtros de fecha
  const handleDateFilterChange = (type: 'created' | 'updated', filterType: DateFilterType, additionalData?: any) => {
    // Primero limpiar todos los parámetros existentes para evitar duplicación
    const clearData: Record<string, string> = {
      [`${type}DateFilter`]: '',
      [`${type}CustomDays`]: '',
      [`${type}NextCustomDays`]: '',
      [`${type}SpecificDate`]: '',
      [`${type}After`]: '',
      [`${type}Before`]: ''
    };

    // Obtener los datos adicionales según el tipo de filtro
    let customDays: number | undefined;
    let nextCustomDays: number | undefined;
    let specificDate: string | undefined;
    let dateRange: { from: string; to: string } | undefined;

    if (filterType === 'customDays') {
      customDays = additionalData ? parseInt(additionalData) : (type === 'created' ? parseInt(customDaysCreated) : parseInt(customDaysUpdated));
    } else if (filterType === 'nextCustomDays') {
      nextCustomDays = additionalData ? parseInt(additionalData) : (type === 'created' ? parseInt(customDaysCreated) : parseInt(customDaysUpdated));
    } else if (filterType === 'specificDate') {
      specificDate = additionalData || (type === 'created' ? specificDateCreated : specificDateUpdated);
    } else if (filterType === 'dateRange') {
      dateRange = additionalData || (type === 'created' ? dateRangeCreated : dateRangeUpdated);
    }

    // Calcular las fechas usando las utilidades
    const dateFilter = calculateDateFilter({
      type: filterType,
      customDays,
      nextCustomDays,
      specificDate,
      dateRange,
    });

    // Crear los datos de actualización con los nuevos valores
    const updateData: Record<string, string> = { ...clearData };
    
    if (dateFilter.after) {
      updateData[`${type}After`] = dateFilter.after;
    }
    if (dateFilter.before) {
      updateData[`${type}Before`] = dateFilter.before;
    }

    // También persistir el tipo de filtro para la UI
    updateData[`${type}DateFilter`] = filterType;
    if (filterType === 'customDays' && customDays) {
      updateData[`${type}CustomDays`] = customDays.toString();
    } else if (filterType === 'nextCustomDays' && nextCustomDays) {
      updateData[`${type}NextCustomDays`] = nextCustomDays.toString();
    } else if (filterType === 'specificDate' && specificDate) {
      updateData[`${type}SpecificDate`] = specificDate;
    }
    // Para dateRange solo guardamos after y before, no dateFrom/dateTo redundantes

    updateFilters(updateData);
  };

  const handleCreate = async () => {
    if (onCreate) {
      setIsCreating(true);
      
      try {
        // Construir los parámetros del filtro
        const filterParams: Record<string, any> = {};
        
        // Agregar filtros de fecha de creación si existen
        if (createdDateType) {
          // Calcular fechas para persistir en el filtro guardado
          const createdFilter = calculateDateFilter({
            type: createdDateType as DateFilterType,
            customDays: customDaysCreated ? parseInt(customDaysCreated) : undefined,
            specificDate: specificDateCreated || undefined,
            dateRange: dateRangeCreated.from && dateRangeCreated.to ? dateRangeCreated : undefined,
          });
          
          if (createdFilter.after) filterParams.createdAfter = createdFilter.after;
          if (createdFilter.before) filterParams.createdBefore = createdFilter.before;
          
          // También guardar el tipo de filtro para la UI
          filterParams.createdDateFilter = createdDateType;
          if (createdDateType === 'customDays' && customDaysCreated) {
            filterParams.createdCustomDays = customDaysCreated;
          } else if (createdDateType === 'specificDate' && specificDateCreated) {
            filterParams.createdSpecificDate = specificDateCreated;
          } else if (createdDateType === 'dateRange') {
            filterParams.createdDateFrom = dateRangeCreated.from;
            filterParams.createdDateTo = dateRangeCreated.to;
          }
        }
        
        // Agregar filtros de fecha de actualización si existen
        if (updatedDateType) {
          // Calcular fechas para persistir en el filtro guardado
          const updatedFilter = calculateDateFilter({
            type: updatedDateType as DateFilterType,
            customDays: customDaysUpdated ? parseInt(customDaysUpdated) : undefined,
            specificDate: specificDateUpdated || undefined,
            dateRange: dateRangeUpdated.from && dateRangeUpdated.to ? dateRangeUpdated : undefined,
          });
          
          if (updatedFilter.after) filterParams.updatedAfter = updatedFilter.after;
          if (updatedFilter.before) filterParams.updatedBefore = updatedFilter.before;
          
          // También guardar el tipo de filtro para la UI
          filterParams.updatedDateFilter = updatedDateType;
          if (updatedDateType === 'customDays' && customDaysUpdated) {
            filterParams.updatedCustomDays = customDaysUpdated;
          } else if (updatedDateType === 'specificDate' && specificDateUpdated) {
            filterParams.updatedSpecificDate = specificDateUpdated;
          } else if (updatedDateType === 'dateRange') {
            filterParams.updatedDateFrom = dateRangeUpdated.from;
            filterParams.updatedDateTo = dateRangeUpdated.to;
          }
        }
        
        // Agregar otros filtros activos desde searchParams
        const allParams = new URLSearchParams(searchParams);
        for (const [key, value] of allParams.entries()) {
          filterParams[key] = value;
        }
        
        const filterData = {
          name,
          isPublic,
          params: filterParams
        };
        
        await onCreate(filterData as any);
      } catch (error) {
        console.error('Error creating filter:', error);
      } finally {
        setIsCreating(false);
      }
    }
  };

  const handleEditCustomField = (fieldBockId: string, fieldName: string, values: string) => {
    const composedFieldName = `${fieldBockId}_${fieldName}`;
    updateFilters({ [composedFieldName]: values });
  };

  // Función para manejar cambios en custom date fields
  const handleCustomDateFieldChange = (fieldBlockId: string, fieldName: string, filterType: DateFilterType, additionalData?: any) => {
    const fieldKey = `${fieldBlockId}_${fieldName}`;
    
    // Primero limpiar todos los parámetros existentes para evitar duplicación
    const clearData: Record<string, string> = {
      [`${fieldKey}_filter`]: '',
      [`${fieldKey}_customDays`]: '',
      [`${fieldKey}_nextCustomDays`]: '',
      [`${fieldKey}_specificDate`]: '',
      [`${fieldKey}_after`]: '',
      [`${fieldKey}_before`]: ''
    };
    
    // Obtener el estado actual del field o crear uno nuevo
    const currentState = customDateFieldStates[fieldKey] || {
      type: '',
      customDays: '',
      nextCustomDays: '',
      specificDate: '',
      dateRange: { from: '', to: '' }
    };

    // Obtener los datos adicionales según el tipo de filtro
    let customDays: number | undefined;
    let nextCustomDays: number | undefined;
    let specificDate: string | undefined;
    let dateRange: { from: string; to: string } | undefined;

    if (filterType === 'customDays') {
      customDays = additionalData ? parseInt(additionalData) : parseInt(currentState.customDays);
    } else if (filterType === 'nextCustomDays') {
      nextCustomDays = additionalData ? parseInt(additionalData) : parseInt(currentState.nextCustomDays);
    } else if (filterType === 'specificDate') {
      specificDate = additionalData || currentState.specificDate;
    } else if (filterType === 'dateRange') {
      dateRange = additionalData || currentState.dateRange;
    }

    // Calcular las fechas usando las utilidades
    const dateFilter = calculateDateFilter({
      type: filterType,
      customDays,
      nextCustomDays,
      specificDate,
      dateRange,
    });

    // Crear los datos de actualización con los nuevos valores
    const updateData: Record<string, string> = { ...clearData };
    
    if (dateFilter.after) {
      updateData[`${fieldKey}_after`] = dateFilter.after;
    }
    if (dateFilter.before) {
      updateData[`${fieldKey}_before`] = dateFilter.before;
    }

    // También persistir el tipo de filtro para la UI
    updateData[`${fieldKey}_filter`] = filterType;
    if (filterType === 'customDays' && customDays) {
      updateData[`${fieldKey}_customDays`] = customDays.toString();
    } else if (filterType === 'nextCustomDays' && nextCustomDays) {
      updateData[`${fieldKey}_nextCustomDays`] = nextCustomDays.toString();
    } else if (filterType === 'specificDate' && specificDate) {
      updateData[`${fieldKey}_specificDate`] = specificDate;
    }
    // Para dateRange solo guardamos after y before, no dateFrom/dateTo redundantes

    updateFilters(updateData);
  };

  // Función para manejar cambios en rangos de fechas sin debounce (para otros tipos)
  const handleDateRangeChange = (type: 'created' | 'updated', field: 'from' | 'to', value: string) => {
    if (type === 'created') {
      const newRange = { ...dateRangeCreated, [field]: value };
      setDateRangeCreated(newRange);
      setDebouncedDateRangeCreated(newRange);
    } else {
      const newRange = { ...dateRangeUpdated, [field]: value };
      setDateRangeUpdated(newRange);
      setDebouncedDateRangeUpdated(newRange);
    }
  };

  // Función para manejar cambios en custom date field ranges
  const handleCustomDateFieldRangeChange = (fieldBlockId: string, fieldName: string, field: 'from' | 'to', value: string) => {
    const fieldKey = `${fieldBlockId}_${fieldName}`;
    const currentState = customDateFieldStates[fieldKey];
    
    if (currentState) {
      const newRange = { ...currentState.dateRange, [field]: value };
      updateCustomDateFieldState(fieldBlockId, fieldName, { dateRange: newRange });
      
      // Actualizar el estado debounced
      setDebouncedCustomDateFieldStates((prev: Record<string, { from: string; to: string }>) => ({
        ...prev,
        [fieldKey]: newRange
      }));
    }
  };

  // Función para manejar cambios en fechas específicas
  const handleSpecificDateChange = (type: 'created' | 'updated', value: string) => {
    if (type === 'created') {
      setSpecificDateCreated(value);
      setDebouncedSpecificDateCreated(value);
    } else {
      setSpecificDateUpdated(value);
      setDebouncedSpecificDateUpdated(value);
    }
  };

  // Función para manejar cambios en custom date field fechas específicas
  const handleCustomDateFieldSpecificDateChange = (fieldBlockId: string, fieldName: string, value: string) => {
    const fieldKey = `${fieldBlockId}_${fieldName}`;
    updateCustomDateFieldState(fieldBlockId, fieldName, { specificDate: value });
    
    // Actualizar el estado debounced
    setDebouncedCustomDateFieldSpecificDates((prev: Record<string, string>) => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  // Función para obtener el estado de un custom date field
  const getCustomDateFieldState = (fieldBlockId: string, fieldName: string) => {
    const fieldKey = `${fieldBlockId}_${fieldName}`;
    return customDateFieldStates[fieldKey] || {
      type: '',
      customDays: '',
      nextCustomDays: '',
      specificDate: '',
      dateRange: { from: '', to: '' }
    };
  };

  // Función para actualizar el estado de un custom date field
  const updateCustomDateFieldState = (fieldBlockId: string, fieldName: string, updates: Partial<{
    type: string;
    customDays: string;
    nextCustomDays: string;
    specificDate: string;
    dateRange: { from: string; to: string };
  }>) => {
    const fieldKey = `${fieldBlockId}_${fieldName}`;
    setCustomDateFieldStates(prev => ({
      ...prev,
      [fieldKey]: {
        ...prev[fieldKey] || { type: '', customDays: '', specificDate: '', dateRange: { from: '', to: '' } },
        ...updates
      }
    }));
  };

  // Función para limpiar un custom date field
  const clearCustomDateField = (fieldBlockId: string, fieldName: string) => {
    const fieldKey = `${fieldBlockId}_${fieldName}`;
    const updateData: Record<string, string> = {
      [`${fieldKey}_filter`]: '',
      [`${fieldKey}_customDays`]: '',
      [`${fieldKey}_nextCustomDays`]: '',
      [`${fieldKey}_specificDate`]: '',
      [`${fieldKey}_after`]: '',
      [`${fieldKey}_before`]: ''
    };
    
    updateFilters(updateData);
    
    // Reset estado local
    setCustomDateFieldStates(prev => ({
      ...prev,
      [fieldKey]: {
        type: '',
        customDays: '',
        nextCustomDays: '',
        specificDate: '',
        dateRange: { from: '', to: '' }
      }
    }));
  };

  const subjectRef = useRef<HTMLInputElement>()

  return (
    <Box flexDirection="column" display="flex" width="400px">
      <Box
        display="flex"
        pl={2}
        pr={2}
        pb={1}
        justifyContent="space-between"
        alignItems="center"
        alignSelf="stretch"
        borderBottom="1px solid var(--divider, rgba(0, 0, 0, 0.12))"
      >
        <Typography variant="subtitle2" gutterBottom={false}>
          {title}
        </Typography>
        {onClose && <IconButton onClick={onClose}>
          <Close />
        </IconButton>}

      </Box>
      {onCreate && (
        <Box pr={2} pl={2} mt={3} display="flex" flexDirection="column" alignItems="flex-start" gap={1} alignSelf="stretch">
          <Typography variant="body2">{t('filters.nameLabel')}</Typography>
          <TextField variant="outlined" size="small" value={name} placeholder={t('filters.namePlaceholder')} onChange={e => setName(e.target.value)} fullWidth />
          <Box display="flex" gap={1} alignItems="center" justifyContent="space-between" width="100%">
            <Box display="flex" gap={1} alignItems="center">
              <Checkbox size="small" color="primary" checked={isPublic} onClick={toggleIsPublic} />
              <Typography variant="body2" >{t('filters.isPublic')}</Typography>
            </Box>
            <Public htmlColor="rgba(0,0,0,.38)" />
          </Box>
          <Divider sx={{ width: '100%' }} />
        </Box>
      )}
      <Box display="flex" flexDirection="column" mt={3} gap={3}>
        <FilterItem
          label={t('cases.list.filterBySubject')}
          onClear={
            () => {
              updateFilters({ subject: '' });
              if (subjectRef.current) {
                subjectRef.current.value = '';
              }
            }}
        >
          <TextField
            inputRef={subjectRef}
            value={searchParams.get('subject')}
            onChange={(e) => {
              updateFilters({ subject: e.currentTarget.value });
            }}
            size="small"
          />
        </FilterItem>

        <FilterItem
          label={t('cases.list.filterByOriginatedByChannel')}
          onClear={
            () => updateFilters({ originatedByChannel: '' })
          }>
          <Autocomplete
            fullWidth
            limitTags={1}
            size="small"
            multiple
            options={originatedByChannelList || []}
            getOptionLabel={(option) => option.name}
            value={
              originatedByChannelList?.filter((option) => searchParams.get('originatedByChannel')?.split('|')?.includes(String(option.id))) || []
            }
            onChange={(_event, value: { id: OriginatedByChannel; name: string }[]) => {
              const valuesForUrl = value.map((val) => val.id).join('|');
              updateFilters({ originatedByChannel: valuesForUrl });
            }}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" fullWidth />
            )}
            renderOption={(props, option, { selected }) => {
              const { ...optionProps } = props;
              return (
                <li {...optionProps} key={option.id}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    sx={{ mr: 1 }}
                    checked={selected}
                  />
                  {option.name}
                </li>
              );
            }}
          />
        </FilterItem>

        <FilterItem
          label={t('cases.list.filterByStatus')}
          onClear={
            () => updateFilters({ status: '' })
          }>
          <Autocomplete
            fullWidth
            limitTags={1}
            size="small"
            multiple
            options={statusList || []}
            getOptionLabel={(option) => option.name}
            value={
              statusList?.filter((option) => searchParams.get('status')?.split('|')?.includes(String(option.id))) || []
            }
            onChange={(_event, value: { id: CaseStatus; name: string }[]) => {
              const valuesForUrl = value.map((val) => val.id).join('|');
              updateFilters({ status: valuesForUrl });
            }}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" fullWidth />
            )}
            renderOption={(props, option, { selected }) => {
              const { ...optionProps } = props;
              return (
                <li {...optionProps} key={option.id}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    sx={{ mr: 1 }}
                    checked={selected}
                  />
                  {option.name}
                </li>
              );
            }}
          />
        </FilterItem>

        <FilterItem
          label={t('cases.list.filterByTeam')}
          onClear={() => updateFilters({ teamIds: '' })}
        >
          <NimbuxAutocomplete<TeamResponseDto>
            data={teams?.items}
            loading={!teams}
            searchParams={searchParams}
            paramKey="teamIds"
            updateFilters={updateFilters}
            onSearchChange={setTeamSearch}
            getOptionLabel={(option) => option.name}
            getOptionId={(option) => String(option.id)}
            notSelectedOption={notSelectedTeamOption as unknown as TeamResponseDto}
            renderOption={(props, option, { selected }) => {
              const { ...optionProps } = props;
              return (
                <li {...optionProps} key={option.id}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    sx={{ mr: 1 }}
                    checked={selected}
                  />
                  {option.name}
                </li>
              );
            }}
          />
        </FilterItem>

        <FilterItem
          label={t('cases.list.filterByWorkflow')}
          onClear={() => updateFilters({ workflowIds: '', stepIds: '' })}
        >
          <NimbuxAutocomplete<WorkflowResponseDto>
            data={workflows?.items}
            loading={!workflows}
            searchParams={searchParams}
            paramKey="workflowIds"
            updateFilters={(filters) => {
              const params: Record<string, string> = { ...filters };
              if (!filters.workflowIds || filters.workflowIds === '') {
                params.stepIds = '';
              }
              updateFilters(params);
            }}
            onSearchChange={setWorkflowSearch}
            getOptionLabel={(option) => option.name}
            getOptionId={(option) => String(option.id)}
            renderOption={(props, option, { selected }) => {
              const { ...optionProps } = props;
              return (
                <li {...optionProps} key={option.id}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    checked={selected}
                  />
                  {option.name}
                </li>
              );
            }}
          />
        </FilterItem>

        <FilterItem
          label={t('cases.list.filterByStep')}
          onClear={() => updateFilters({ stepIds: '' })}
        >
          <Box sx={{ opacity: !searchParams.get('workflowIds') ? 0.5 : 1 }}>
            <NimbuxAutocomplete<StepOptionsResponseDto>
              data={steps}
              loading={!steps}
              searchParams={searchParams}
              paramKey="stepIds"
              updateFilters={updateFilters}
              onSearchChange={setStepSearch}
              getOptionLabel={(option) => option.name}
              getOptionId={(option) => String(option.id)}
              renderOption={(props, option, { selected }) => {
                const { ...optionProps } = props;
                return (
                  <li {...optionProps} key={option.id}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlank fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      sx={{ mr: 1 }}
                      checked={selected}
                    />
                    {option.name}
                  </li>
                );
              }}
            />
          </Box>
        </FilterItem>

        <FilterItem
          label={t('cases.list.filterByAgent')}
          onClear={() => updateFilters({ agentIds: '' })}
        >
          <NimbuxAutocomplete<UserListResponseDto>
            data={agentList?.items}
            loading={!agentList}
            searchParams={searchParams}
            paramKey="agentIds"
            updateFilters={updateFilters}
            onSearchChange={setAgentSearch}
            getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
            getOptionId={(option) => option.id}
            notSelectedOption={notSelectedOption as UserListResponseDto}
            renderOption={(props, option, { selected }) => {
              const { ...optionProps } = props;
              return (
                <li {...optionProps} key={option.id}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    sx={{ mr: 1 }}
                    checked={selected}
                  />
                  {option.firstName} {option.lastName}
                </li>
              );
            }}
          />
        </FilterItem>

        <FilterItem
          label={t('cases.list.filterByClient')}
          onClear={() => updateFilters({ clientIds: '' })}
        >
          <NimbuxAutocomplete<ClientResponseDto>
            data={clientList?.items}
            loading={!clientList}
            searchParams={searchParams}
            paramKey="clientIds"
            updateFilters={updateFilters}
            onSearchChange={setClientSearch}
            getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
            getOptionId={(option) => option.id}
            renderOption={(props, option, { selected }) => {
              const { ...optionProps } = props;
              return (
                <li {...optionProps} key={option.id}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    sx={{ mr: 1 }}
                    checked={selected}
                  />
                  {option.firstName} {option.lastName}
                </li>
              );
            }}
          />
        </FilterItem>

        <FilterItem
          label={t('cases.list.filterByOrganization')}
          onClear={
            () => updateFilters({ organizations: '' })
          }>
          <Autocomplete
            fullWidth
            limitTags={1}
            size="small"
            multiple
            options={organizations || []}
            getOptionLabel={(option) => option.name}
            value={
              organizations?.filter((option) => searchParams.get('organizations')?.split('|')?.includes(String(option.id))) || []
            }
            onChange={(_event, value: { id: number; name: string }[]) => {
              const valuesForUrl = value.map((val) => val.id).join('|');
              updateFilters({ organizations: valuesForUrl });
            }}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" fullWidth />
            )}
            renderOption={(props, option, { selected }) => {
              const { ...optionProps } = props;
              return (
                <li {...optionProps} key={option.id}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    sx={{ mr: 1 }}
                    checked={selected}
                  />
                  {option.name}
                </li>
              );
            }}
          />
        </FilterItem>

        <FilterItem
          label={t('cases.list.filterByAgency')}
          onClear={
            () => updateFilters({ agencies: '' })
          }>
          <Autocomplete
            fullWidth
            limitTags={1}
            size="small"
            multiple
            options={agencies || []}
            getOptionLabel={(option) => option.name}
            value={
              agencies ? agencies?.filter((option) => searchParams.get('agencies')?.split('|')?.includes(String(option.id))) || [] : []
            }
            onChange={(_event, value: { id: number; name: string }[]) => {
              const valuesForUrl = value.map((val) => val.id).join('|');
              updateFilters({ agencies: valuesForUrl });
            }}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" fullWidth />
            )}
            renderOption={(props, option, { selected }) => {
              const { ...optionProps } = props;
              return (
                <li {...optionProps} key={option.id}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    sx={{ mr: 1 }}
                    checked={selected}
                  />
                  {option.name}
                </li>
              );
            }}
          />
        </FilterItem>

        <FilterItem
          label={t('cases.list.filterByCreated')}
          onClear={() => {
            updateFilters({ 
              createdDateFilter: '', 
              createdCustomDays: '', 
              createdSpecificDate: '',
              createdDateFrom: '',
              createdDateTo: '',
              createdAfter: '',
              createdBefore: ''
            });
            setCreatedDateType('');
            setCustomDaysCreated('');
            setSpecificDateCreated('');
            setDateRangeCreated({ from: '', to: '' });
          }}
        >
          <Box>
            <Select
              fullWidth
              size="small"
              value={createdDateType}
              onChange={(e) => {
                const value = e.target.value;
                setCreatedDateType(value);
                if (value) {
                  handleDateFilterChange('created', value as DateFilterType);
                }
              }}
              displayEmpty
            >
              <MenuItem value="" disabled>
                {t('filters.date.selectDateType')}
              </MenuItem>
              {pastDateFilterOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            
            {createdDateType === 'customDays' && (
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder={t('filters.date.numberOfDays')}
                value={customDaysCreated}
                onChange={(e) => {
                  setCustomDaysCreated(e.target.value);
                  handleDateFilterChange('created', 'customDays', e.target.value);
                }}
                sx={{ mt: 1 }}
              />
            )}
            
            {createdDateType === 'specificDate' && (
              <TextField
                fullWidth
                size="small"
                type="date"
                value={specificDateCreated}
                onChange={(e) => handleSpecificDateChange('created', e.target.value)}
                sx={{ mt: 1 }}
              />
            )}
            
            {createdDateType === 'dateRange' && (
              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexDirection: 'column' }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label={t('filters.date.from')}
                  value={dateRangeCreated.from}
                  onChange={(e) => handleDateRangeChange('created', 'from', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label={t('filters.date.to')}
                  value={dateRangeCreated.to}
                  onChange={(e) => handleDateRangeChange('created', 'to', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            )}
          </Box>
        </FilterItem>

        <FilterItem
          label={t('cases.list.filterByUpdated')}
          onClear={() => {
            updateFilters({ 
              updatedDateFilter: '', 
              updatedCustomDays: '', 
              updatedSpecificDate: '',
              updatedDateFrom: '',
              updatedDateTo: '',
              updatedAfter: '',
              updatedBefore: ''
            });
            setUpdatedDateType('');
            setCustomDaysUpdated('');
            setSpecificDateUpdated('');
            setDateRangeUpdated({ from: '', to: '' });
          }}
        >
          <Box>
            <Select
              fullWidth
              size="small"
              value={updatedDateType}
              onChange={(e) => {
                const value = e.target.value;
                setUpdatedDateType(value);
                if (value) {
                  handleDateFilterChange('updated', value as DateFilterType);
                }
              }}
              displayEmpty
            >
              <MenuItem value="" disabled>
                {t('filters.date.selectDateType')}
              </MenuItem>
              {pastDateFilterOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            
            {updatedDateType === 'customDays' && (
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder={t('filters.date.numberOfDays')}
                value={customDaysUpdated}
                onChange={(e) => {
                  setCustomDaysUpdated(e.target.value);
                  handleDateFilterChange('updated', 'customDays', e.target.value);
                }}
                sx={{ mt: 1 }}
              />
            )}
            
            {updatedDateType === 'specificDate' && (
              <TextField
                fullWidth
                size="small"
                type="date"
                value={specificDateUpdated}
                onChange={(e) => handleSpecificDateChange('updated', e.target.value)}
                sx={{ mt: 1 }}
              />
            )}
            
            {updatedDateType === 'dateRange' && (
              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexDirection: 'column' }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label={t('filters.date.from')}
                  value={dateRangeUpdated.from}
                  onChange={(e) => handleDateRangeChange('updated', 'from', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label={t('filters.date.to')}
                  value={dateRangeUpdated.to}
                  onChange={(e) => handleDateRangeChange('updated', 'to', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            )}
          </Box>
        </FilterItem>

        {customFields?.map(fieldBlock => (
          <Box key={fieldBlock.id} display="flex" flexDirection="column" pr={2} pl={2} gap={1}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" alignSelf="stretch">
              <Typography variant="subtitle2" gutterBottom={false}>{fieldBlock.name}</Typography>
            </Box>
            {fieldBlock.fields?.map(field => {
              const customDateState = getCustomDateFieldState(fieldBlock.id, field.name);
              
              return (
                <Box key={`${fieldBlock.id}_${field.name}`}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" alignSelf="stretch">
                    <Typography variant="body2" gutterBottom={false}>{t('filters.filterBy')} {field.label}</Typography>
                    <Link 
                      color="error" 
                      underline="hover" 
                      onClick={() => {
                        if (field.fieldType === 'date') {
                          clearCustomDateField(fieldBlock.id, field.name);
                        } else {
                          handleEditCustomField(fieldBlock.id, field.name, '');
                        }
                      }}
                    >
                      {t('filters.clear')}
                    </Link>
                  </Box>
                  
                  {/* Filtro de fecha avanzado para custom fields */}
                  {(field.fieldType === 'date') && (
                    <Box sx={{ mt: 1 }}>
                      <Select
                        fullWidth
                        size="small"
                        value={customDateState.type}
                        onChange={(e) => {
                          const value = e.target.value;
                          updateCustomDateFieldState(fieldBlock.id, field.name, { type: value });
                          if (value) {
                            handleCustomDateFieldChange(fieldBlock.id, field.name, value as DateFilterType);
                          }
                        }}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>
                          {t('filters.date.selectDateType')}
                        </MenuItem>
                        {allDateFilterOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      
                      {customDateState.type === 'customDays' && (
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          placeholder={t('filters.date.numberOfDays')}
                          value={customDateState.customDays}
                          onChange={(e) => {
                            updateCustomDateFieldState(fieldBlock.id, field.name, { customDays: e.target.value });
                            handleCustomDateFieldChange(fieldBlock.id, field.name, 'customDays', e.target.value);
                          }}
                          sx={{ mt: 1 }}
                        />
                      )}
                      
                      {customDateState.type === 'nextCustomDays' && (
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          placeholder={t('filters.date.numberOfDays')}
                          value={customDateState.nextCustomDays}
                          onChange={(e) => {
                            const fieldKey = `${fieldBlock.id}_${field.name}`;
                            updateCustomDateFieldState(fieldBlock.id, field.name, { nextCustomDays: e.target.value });
                            setDebouncedCustomDateFieldNextCustomDays((prev: Record<string, string>) => ({
                              ...prev,
                              [fieldKey]: e.target.value
                            }));
                          }}
                          sx={{ mt: 1 }}
                        />
                      )}
                      
                      {customDateState.type === 'specificDate' && (
                        <TextField
                          fullWidth
                          size="small"
                          type="date"
                          value={customDateState.specificDate}
                          onChange={(e) => handleCustomDateFieldSpecificDateChange(fieldBlock.id, field.name, e.target.value)}
                          sx={{ mt: 1 }}
                        />
                      )}
                      
                      {customDateState.type === 'dateRange' && (
                        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexDirection: 'column' }}>
                          <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label={t('filters.date.from')}
                            value={customDateState.dateRange.from}
                            onChange={(e) => handleCustomDateFieldRangeChange(fieldBlock.id, field.name, 'from', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label={t('filters.date.to')}
                            value={customDateState.dateRange.to}
                            onChange={(e) => handleCustomDateFieldRangeChange(fieldBlock.id, field.name, 'to', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Box>
                      )}
                    </Box>
                  )}
                  
                  {/* Otros tipos de campos (no date) */}
                  {(field.fieldType === 'string' || field.fieldType === 'longtext') && <TextField
                    value={searchParams.get(`${fieldBlock.id}_${field.name}`) || ''}
                    size="small"
                    fullWidth
                    onChange={(e) => handleEditCustomField(fieldBlock.id, field.name, e.target.value)}
                  />}
                  
                  {(field.fieldType === 'select' || field.fieldType === 'multiselect') &&
                    <FormControl fullWidth margin="normal">
                      <Select
                        displayEmpty
                        size="small"
                        value={searchParams.get(`${fieldBlock.id}_${field.name}`) || ''}
                        onChange={(e) => handleEditCustomField(fieldBlock.id, field.name, e.target.value)}
                        required={field.required}
                      >
                        <MenuItem key={field.label} value={undefined} disabled>
                          {field.label}
                        </MenuItem>
                        {field.options?.map((option, index) => (
                          <MenuItem key={index} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  }
                </Box>
              );
            })}
          </Box>
        ))}


      </Box>
      <Box display="flex" p={2} mt={3} justifyContent="space-between" alignItems="flex-start" alignSelf="stretch" borderTop="1px solid var(--divider, rgba(0, 0, 0, 0.12))">
        <Button onClick={handleClearFilters} size="medium" color="error" variant="text">{t('filters.clearAll')}</Button>
        {onCreate && <LoadingButton loading={isCreating} size="medium" color="primary" variant="contained" onClick={handleCreate}>{t('common.actions.add')}</LoadingButton>}
      </Box>
    </Box>
  );
};
